import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import FloatingInput from "../common/FloatingInput";
import {
  censusBillingRules,
  getCensusPayors,
  censusGroupPlanAction,
  censusServiceConfigs,
} from "../services/soar";

function CensusRules(props) {
  const [rules, setRules] = useState([]);
  const [setupData, setSetupData] = useState([]);
  const [status, setStatus] = useState("incomplete");
  const [filters, setFilters] = useState({ search: "" });
  const applicationId =
    process.env.REACT_APP_ENVIRONMENT === "Production"
      ? "5dae139e-d052-4bd8-bf4a-499a31ac3c15"
      : "58990f83-8566-4ba8-b4b5-584a64e4cba1";
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const navigate = useNavigate();

  useEffect(() => {
    getRules();
    getSetupData();
  }, []);

  const getSetupData = async () => {
    const payorResponse = await getCensusPayors();
    const groupPlansResponse = await censusGroupPlanAction({
      command: "query",
      keyword: "viewall",
      application: applicationId,
    });
    const serviceConfigResponse = await censusServiceConfigs({
      command: "query",
      application: applicationId,
      keyword: "viewall",
    });
    setSetupData({
      ...setupData,
      payors: payorResponse
        .filter((payor) => payor.active)
        .map((payor) => ({
          _id: payor.id,
          name: payor.name,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      groupPlans: groupPlansResponse.data
        .filter((groupPlan) => groupPlan.active)
        .map((groupPlan) => ({
          _id: groupPlan.id,
          name: groupPlan.name,
          payorId: groupPlan.payorId,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
      serviceConfigurations: serviceConfigResponse.data
        .filter((serviceConfig) => serviceConfig.active)
        .map((serviceConfig) => ({
          _id: serviceConfig.id,
          name: serviceConfig.name,
          config: serviceConfig.configuration,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    });
  };

  const getRules = async () => {
    // console.log("Setup Data", setupData);
    const response = await censusBillingRules({
      command: "query",
      application: applicationId,
      keyword: "viewall",
    });
    if (response.code === "success") {
      setRules(
        response.data
          .filter((rule) => rule.active && !rule.clientId)
          .sort((a, b) => a.payorName.localeCompare(b.payorName))
      );
    } else {
      toast.error(`Error loading Census Rules: ${response.message}`);
    }
  };

  useMemo(() => {
    if (
      filters.payor &&
      filters.payor !== "" &&
      filters.groupPlan &&
      filters.groupPlan !== "" &&
      filters.serviceConfiguration &&
      filters.serviceConfiguration !== ""
    ) {
      toast.info("Searching for duplicates");
      const duplicate = rules.find(
        (rule) =>
          rule.payorId === filters.payor &&
          rule.groupPlanId === filters.groupPlan &&
          rule.serviceConfigurationId === filters.serviceConfiguration &&
          rule.active &&
          !rule.expiration
      );
      console.log("Duplicate Rule Check", duplicate);
      if (duplicate) {
        toast.error("This rule already exists.");
        setStatus("duplicate");
        return; // exit if a duplicate rule is found
      } else {
        setStatus("complete");
        return;
      }
    } else {
      setStatus("incomplete");
    }
  }, [filters]);

  const saveRule = async () => {
    const body = {
      application: applicationId,
      command: "insert",
      type: "Census",
      payor: filters.payor,
      groupPlan: filters.groupPlan,
      serviceConfiguration: filters.serviceConfiguration,
      configuration: {},
    };

    const response = await censusBillingRules(body);

    if (response.code === "success") {
      toast.success("Rule saved successfully!");
      handleClose();
      navigate(`/rev/rules/${response.id}`);
    } else {
      toast.error(`Error saving rule: ${response.message}`);
    }
  };

  const ruleAction = async (id, status) => {
    const body = {
      command: status,
      application: applicationId,
      id: id,
    };
    const response = await censusBillingRules(body);

    if (response.code === "success") {
      toast.success(
        `Rule ${
          status === "deactivate" ? "deactivated" : "activated"
        } successfully!`
      );
      getRules();
    } else {
      toast.error(
        `Error ${
          status === "deactivate" ? "deactivating" : "activating"
        } rule: ${response.message}`
      );
    }
  };

  const handleClose = () => {
    setFilters({
      payor: "",
      groupPlan: "",
      serviceConfiguration: "",
      search: "",
    });
    setStatus("incomplete");
  };
  return (
    <>
      <div className="container-fluid px-0">
        <div className="h4 mb-4">Census Billing Rules</div>
        <div className="row mb-4">
          <div className="col-md-6 mb-3 mb-md-0">
            <div className="row">
              <div className="col-10">
                <FloatingInput
                  type="text"
                  name="Search"
                  label="Search Payor and Service Configuration"
                  placeholder="Search"
                  onChange={(e) => {
                    setFilters({ ...filters, search: e.target.value });
                  }}
                  value={filters?.search}
                />
              </div>
              <div className="col-2 d-flex align-items-center justify-content-center">
                <i
                  className="fas fa-times-circle fa-xl text-danger"
                  onClick={() => setFilters({ ...filters, search: "" })}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="h5 mb-3">Filters</div>
        <div className="row g-3 mb-4">
          <div className="col-md-4 mb-2">
            <FloatingInput
              type="select"
              name="filterPayor"
              label="Payor"
              options={setupData?.payors?.filter((i) =>
                filters?.search !== "" &&
                (filters.payor == null || filters.payor === "")
                  ? i.name
                      .toUpperCase()
                      .includes(filters?.search?.toUpperCase())
                  : i
              )}
              value={filters.payor}
              onChange={(e) =>
                setFilters({ ...filters, payor: e.target.value, search: "" })
              }
            />
          </div>
          <div className="col-md-4 mb-2">
            <FloatingInput
              type="select"
              name="filterGroupPlan"
              label="Group Plan"
              options={setupData?.groupPlans?.filter(
                (i) =>
                  (filters?.search !== "" &&
                  (filters.groupPlan == null || filters.groupPlan === "")
                    ? i.name
                        .toUpperCase()
                        .includes(filters?.search?.toUpperCase())
                    : i) &&
                  (filters.payor !== "" ? i.payorId === filters.payor : i)
              )}
              value={filters.groupPlan}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  groupPlan: e.target.value,
                  search: "",
                })
              }
            />
          </div>
          <div className="col-md-3 mb-2">
            <FloatingInput
              type="select"
              name="filterServiceConfiguration"
              label="Service Configuration"
              options={setupData?.serviceConfigurations?.filter((i) =>
                filters?.search !== "" &&
                (filters.serviceConfiguration == null ||
                  filters.serviceConfiguration === "")
                  ? i.name
                      .toUpperCase()
                      .includes(filters?.search?.toUpperCase())
                  : i
              )}
              value={filters.serviceConfiguration}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  serviceConfiguration: e.target.value,
                  search: "",
                })
              }
            />
          </div>
          <div className="col-md-1 d-flex align-items-center justify-content-center justify-content-md-start mb-3">
            <i
              className="fas fa-circle-xmark fa-2x text-danger me-1"
              onClick={() =>
                setFilters({
                  ...filters,
                  payor: "",
                  groupPlan: "",
                  serviceConfiguration: "",
                  search: "",
                })
              }
            ></i>
            {(safyInfo.user.modules.includes("Admin") ||
              safyInfo.user.modules.includes("Rev Cycle")) &&
            status === "complete" ? (
              <div className="btn btn-success" onClick={() => saveRule()}>
                Save
              </div>
            ) : null}
          </div>
        </div>
        <div className="row fw-bold mb-3 d-none d-md-flex">
          <div className="col-md-3">Payor</div>
          <div className="col-md-4">Group Plan</div>
          <div className="col-md-2">Service Configuration</div>
          <div className="col-md-2">Status</div>
          <div className="col-md-1">Actions</div>
        </div>

        {rules
          ?.filter(
            (r) =>
              r.active &&
              (filters.payor ? r.payorId === filters.payor : r) &&
              (filters.groupPlan ? r.groupPlanId === filters.groupPlan : r) &&
              (filters.serviceConfiguration
                ? r.serviceConfigurationId === filters.serviceConfiguration
                : r) &&
              (!r.expiration || r.expiration === "")
          )
          .map((rule) => (
            <div
              className={`row rounded border border-dark ${
                rule.draft ? "bg-warning" : "bg-light"
              } p-2 mb-3`}
              key={rule.id}
            >
              <div className="col-12 col-md-3 mb-2 mb-md-0">
                <span className="fw-bold d-md-none">Payor: </span>
                {rule.payorName}
              </div>
              <div className="col-12 col-md-4 mb-2 mb-md-0">
                <span className="fw-bold d-md-none">Group Plan: </span>
                {rule.groupPlanName}
              </div>
              <div className="col-12 col-md-2 mb-2 mb-md-0">
                <span className="fw-bold d-md-none">
                  Service Configuration:{" "}
                </span>
                {rule.serviceConfigurationName}
              </div>
              <div className="col-12 col-md-2 mb-2 mb-md-0">
                <span className="fw-bold d-md-none">Status: </span>
                {rule.draft
                  ? "Draft"
                  : `Published: ${new Date(
                      rule.effective
                    ).toLocaleDateString()}`}
              </div>
              <div className="col-12 col-md-1 text-center text-md-start">
                <i
                  className={`fas fa-${
                    rule.active ? "trash" : "trash-arrow-up"
                  } text-${rule.active ? "danger" : "success"} mx-2 mx-md-1`}
                  role="button"
                  onClick={() =>
                    ruleAction(
                      rule.id,
                      rule.active ? "deactivate" : "reactivate"
                    )
                  }
                />
                <i
                  className={`fas ${
                    rule.draft ? "fa-pen" : "fa-eye"
                  } text-primary mx-2 mx-md-1`}
                  role="button"
                  onClick={() => navigate(`/rev/rules/${rule.id}`)}
                ></i>
              </div>
            </div>
          ))}
      </div>
    </>
  );
}

export default CensusRules;
